import type { OtaCheckResponse } from '@cosvision/shared';
import { isNativePlatform } from './platform.js';
import { request } from './api.js';

/**
 * Self-hosted OTA web-bundle updates (M5), native-only. Manual/prompted flow:
 *   1. on launch, ask our backend /app/ota/check (current bundle + native build)
 *   2. if a newer bundle exists, download + SHA-256-verify it in the background
 *   3. surface a "Restart to apply" prompt; on tap, `set()` swaps the bundle.
 *
 * `notifyOtaAppReady()` (called from main.tsx on every launch) is the rollback
 * guard: a freshly-applied bundle that never reaches it is reverted by the
 * plugin on the next launch.
 *
 * Plugin proxy gotcha (carried from secure-storage / push): never RETURN a
 * Capacitor plugin proxy from an async function — only call methods on it.
 * Everything here resolves plain objects.
 */
async function updaterModule() {
  return import('@capgo/capacitor-updater');
}

async function appModule() {
  return import('@capacitor/app');
}

export async function notifyOtaAppReady(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { CapacitorUpdater } = await updaterModule();
    await CapacitorUpdater.notifyAppReady();
  } catch (err) {
    console.warn('OTA notifyAppReady failed', err);
  }
}

export interface ReadyOtaUpdate {
  /** Opaque plugin bundle id to activate. */
  id: string;
  version: string;
}

/**
 * Check for and download a newer bundle. Returns the downloaded-and-verified
 * bundle ready to apply, or null when there's nothing new (or on any error —
 * OTA must never break the running app). The download is SHA-256 verified by
 * the plugin against the checksum our server returns.
 */
export async function checkAndDownloadOta(): Promise<ReadyOtaUpdate | null> {
  if (!isNativePlatform()) return null;
  try {
    const { CapacitorUpdater } = await updaterModule();
    const { App } = await appModule();

    const current = await CapacitorUpdater.current();
    const info = await App.getInfo();
    // The version we report: the active OTA bundle, or the native versionName
    // on a fresh install / after a reset to the built-in bundle.
    const builtIn = !current.bundle?.version || current.bundle.version === 'builtin';
    const currentVersion = builtIn ? info.version : current.bundle.version;

    const res = await request<OtaCheckResponse>(
      'get',
      `/app/ota/check?currentVersion=${encodeURIComponent(currentVersion)}&nativeBuild=${encodeURIComponent(info.build)}`,
    );
    if (!res.updateAvailable || !res.url || !res.version || !res.checksum) return null;

    const bundle = await CapacitorUpdater.download({
      url: res.url,
      version: res.version,
      checksum: res.checksum,
    });
    return { id: bundle.id, version: bundle.version };
  } catch (err) {
    console.warn('OTA check/download failed', err);
    return null;
  }
}

/** Activate a downloaded bundle. Reloads the WebView with the new bundle. */
export async function applyOtaUpdate(id: string): Promise<void> {
  if (!isNativePlatform()) return;
  const { CapacitorUpdater } = await updaterModule();
  await CapacitorUpdater.set({ id });
}
